/**
 * Carinski AI Asistent - Internationalization (i18n)
 * 
 * This module provides multi-language support for the application.
 * Supports Bosnian (Latin and Cyrillic), English, German, and Serbian.
 */

/**
 * Supported languages
 */
export type SupportedLanguage = "bs" | "bs-Cyrl" | "en" | "de" | "sr" | "sr-Cyrl" | "fr" | "sk" | "it" | "mk" | "ru" | "es";

/**
 * Translation keys structure
 */
export interface TranslationKeys {
  // Common
  appName: string;
  welcome: string;
  loading: string;
  error: string;
  success: string;
  cancel: string;
  confirm: string;
  save: string;
  delete: string;
  edit: string;
  search: string;
  
  // Navigation
  home: string;
  classify: string;
  searchHs: string;
  agreements: string;
  reports: string;
  admin: string;
  settings: string;
  logout: string;
  
  // Classification
  productDescription: string;
  additionalInfo: string;
  classifyProduct: string;
  classificationResult: string;
  hsCode: string;
  confidence: string;
  confidenceHigh: string;
  confidenceMedium: string;
  confidenceLow: string;
  confidenceNone: string;
  legalBasis: string;
  citations: string;
  girPath: string;
  validationReasons: string;
  
  // Validation status
  statusFinal: string;
  statusStop: string;
  
  // Errors
  errorInvalidHsCode: string;
  errorNoMatch: string;
  errorPrecedentConflict: string;
  errorHierarchy: string;
  
  // Admin
  tenantManagement: string;
  userManagement: string;
  auditLogs: string;
  importData: string;

  // Chat UI
  chatTitle: string;
  chatSubtitle: string;
  welcomeTitle: string;
  welcomeMessage: string;
  welcomeImageHint: string;
  welcomeExample: string;
  newClassification: string;
  sendButton: string;
  inputPlaceholder: string;
  inputPlaceholderAdditional: string;
  linkDetected: string;
  exportPdf: string;
  classificationSuccess: string;
  statusWarning: string;
  reasoningLabel: string;
  girRulesLabel: string;
  legalBasisLabel: string;
  alternativeCodes: string;
  modeLabel: string;
  fromCache: string;
  processingTime: string;
  toolsUsed: string;
  errorClassify: string;
  errorGeneric: string;
  imageSelectError: string;
  imageSizeError: string;
  popupBlocked: string;

  // History sidebar
  history: string;
  searchHistory: string;
  noHistory: string;
  reExportPdf: string;

  // Confidence visualization
  confidenceFinal: string;
  confidenceFinalWarning: string;

  // Drag and drop
  dragDropHint: string;
  dragDropActive: string;

  // Language switcher
  language: string;

  // Classification labels
  proposedHsCode: string;
  aiGeneratedDisclaimer: string;
}

/**
 * Bosnian (Latin) translations
 */
export const bs: TranslationKeys = {
  // Common
  appName: "Carinski AI Asistent",
  welcome: "Dobrodošli",
  loading: "Učitavanje...",
  error: "Greška",
  success: "Uspješno",
  cancel: "Otkaži",
  confirm: "Potvrdi",
  save: "Sačuvaj",
  delete: "Obriši",
  edit: "Uredi",
  search: "Pretraži",
  
  // Navigation
  home: "Početna",
  classify: "Klasifikacija",
  searchHs: "Pretraga HS kodova",
  agreements: "Sporazumi",
  reports: "Izvještaji",
  admin: "Administracija",
  settings: "Postavke",
  logout: "Odjava",
  
  // Classification
  productDescription: "Opis proizvoda",
  additionalInfo: "Dodatne informacije",
  classifyProduct: "Klasifikuj proizvod",
  classificationResult: "Rezultat klasifikacije",
  hsCode: "HS kod",
  confidence: "Pouzdanost",
  confidenceHigh: "Visoka",
  confidenceMedium: "Srednja",
  confidenceLow: "Niska",
  confidenceNone: "Nedovoljna",
  legalBasis: "Pravna osnova",
  citations: "Citati",
  girPath: "GIR pravila",
  validationReasons: "Razlozi validacije",
  
  // Validation status
  statusFinal: "Konačno",
  statusStop: "Zaustavljeno",
  
  // Errors
  errorInvalidHsCode: "Predloženi HS kod ne postoji u nomenklaturi",
  errorNoMatch: "Opis proizvoda ne odgovara tarifnom broju",
  errorPrecedentConflict: "Precedenti sugeriraju drugačiju klasifikaciju",
  errorHierarchy: "Hijerarhijska struktura tarife je narušena",
  
  // Admin
  tenantManagement: "Upravljanje zakupcima",
  userManagement: "Upravljanje korisnicima",
  auditLogs: "Revizorski zapisi",
  importData: "Uvoz podataka",

  // Chat UI
  chatTitle: "Carinski Asistent",
  chatSubtitle: "Brza i precizna HS klasifikacija proizvoda",
  welcomeTitle: "👋 Dobrodošli!",
  welcomeMessage: "Opišite proizvod koji želite klasificirati, a ja ću vam pomoći da pronađete odgovarajući HS (Harmonized System) tarifni broj.",
  welcomeImageHint: "📷 Možete priložiti i sliku proizvoda za precizniju klasifikaciju.",
  welcomeExample: '💡 Primjer: "Čelična cijev, bešavna, prečnika 50mm, za građevinarstvo"',
  newClassification: "+ Nova klasifikacija",
  sendButton: "Pošalji",
  inputPlaceholder: "Opišite proizvod za klasifikaciju...",
  inputPlaceholderAdditional: "Unesite dodatne informacije...",
  linkDetected: "🔗 Link detektovan - analiziram stranicu proizvoda",
  exportPdf: "📄 Export PDF",
  classificationSuccess: "✅ PRIJEDLOG KLASIFIKACIJE",
  statusWarning: "⚠️ Status: PROCJENA SA UPOZORENJEM",
  reasoningLabel: "📋 Obrazloženje",
  girRulesLabel: "⚖️ GIR pravila",
  legalBasisLabel: "📜 Pravna osnova",
  alternativeCodes: "🔄 Alternativni kodovi",
  modeLabel: "🔧 Mod",
  fromCache: "iz cache memorije",
  processingTime: "⏱️ Vrijeme obrade",
  toolsUsed: "🛠️ Korišteni alati",
  errorClassify: "⚠️ Nije moguće klasificirati proizvod. Pokušajte sa detaljnijim opisom.",
  errorGeneric: "❌ Došlo je do greške. Molimo pokušajte ponovo.",
  imageSelectError: "Molimo odaberite sliku (JPEG, PNG, WebP)",
  imageSizeError: "Slika je prevelika. Maksimalna veličina je 5MB.",
  popupBlocked: "Popup prozor je blokiran. Molimo omogućite popupe za ovu stranicu.",

  // History sidebar
  history: "Historija",
  searchHistory: "Pretraži po HS kodu, opisu, datumu...",
  noHistory: "Nema prethodnih klasifikacija.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "PROCJENA",
  confidenceFinalWarning: "PROCJENA SA UPOZORENJEM",

  // Drag and drop
  dragDropHint: "Ili prevucite sliku ovdje",
  dragDropActive: "Pustite sliku ovdje...",

  // Language switcher
  language: "Jezik",

  // Classification labels
  proposedHsCode: "Predloženi HS Code",
  aiGeneratedDisclaimer: "⚠️ Ova klasifikacija je AI-generisana i informativnog je karaktera. Nije pravno obavezujuća. Prije korištenja obavezno provjerite sa nadležnim carinskim organom ili carinskim stručnjakom.",
};

/**
 * Bosnian (Cyrillic) translations
 */
export const bsCyrl: TranslationKeys = {
  // Common
  appName: "Царински АИ Асистент",
  welcome: "Добродошли",
  loading: "Учитавање...",
  error: "Грешка",
  success: "Успјешно",
  cancel: "Откажи",
  confirm: "Потврди",
  save: "Сачувај",
  delete: "Обриши",
  edit: "Уреди",
  search: "Претражи",
  
  // Navigation
  home: "Почетна",
  classify: "Класификација",
  searchHs: "Претрага ХС кодова",
  agreements: "Споразуми",
  reports: "Извјештаји",
  admin: "Администрација",
  settings: "Поставке",
  logout: "Одјава",
  
  // Classification
  productDescription: "Опис производа",
  additionalInfo: "Додатне информације",
  classifyProduct: "Класификуј производ",
  classificationResult: "Резултат класификације",
  hsCode: "ХС код",
  confidence: "Поузданост",
  confidenceHigh: "Висока",
  confidenceMedium: "Средња",
  confidenceLow: "Ниска",
  confidenceNone: "Недовољна",
  legalBasis: "Правна основа",
  citations: "Цитати",
  girPath: "ГИР правила",
  validationReasons: "Разлози валидације",
  
  // Validation status
  statusFinal: "Коначно",
  statusStop: "Заустављено",
  
  // Errors
  errorInvalidHsCode: "Предложени ХС код не постоји у номенклатури",
  errorNoMatch: "Опис производа не одговара тарифном броју",
  errorPrecedentConflict: "Преседани сугерирају другачију класификацију",
  errorHierarchy: "Хијерархијска структура тарифе је нарушена",
  
  // Admin
  tenantManagement: "Управљање закупцима",
  userManagement: "Управљање корисницима",
  auditLogs: "Ревизорски записи",
  importData: "Увоз података",

  // Chat UI
  chatTitle: "Царински Асистент",
  chatSubtitle: "Брза и прецизна ХС класификација производа",
  welcomeTitle: "👋 Добродошли!",
  welcomeMessage: "Опишите производ који желите класификовати, а ја ћу вам помоћи да пронађете одговарајући ХС тарифни број.",
  welcomeImageHint: "📷 Можете приложити и слику производа за прецизнију класификацију.",
  welcomeExample: '💡 Примјер: "Челична цијев, бешавна, пречника 50мм, за грађевинарство"',
  newClassification: "+ Нова класификација",
  sendButton: "Пошаљи",
  inputPlaceholder: "Опишите производ за класификацију...",
  inputPlaceholderAdditional: "Унесите додатне информације...",
  linkDetected: "🔗 Линк детектован - анализирам страницу производа",
  exportPdf: "📄 Извоз ПДФ",
  classificationSuccess: "✅ ПРИЈЕДЛОГ КЛАСИФИКАЦИЈЕ",
  statusWarning: "⚠️ Статус: ПРОЦЈЕНА СА УПОЗОРЕЊЕМ",
  reasoningLabel: "📋 Образложење",
  girRulesLabel: "⚖️ ГИР правила",
  legalBasisLabel: "📜 Правна основа",
  alternativeCodes: "🔄 Алтернативни кодови",
  modeLabel: "🔧 Мод",
  fromCache: "из кеш меморије",
  processingTime: "⏱️ Вријеме обраде",
  toolsUsed: "🛠️ Коришћени алати",
  errorClassify: "⚠️ Није могуће класификовати производ. Покушајте са детаљнијим описом.",
  errorGeneric: "❌ Дошло је до грешке. Молимо покушајте поново.",
  imageSelectError: "Молимо одаберите слику (JPEG, PNG, WebP)",
  imageSizeError: "Слика је превелика. Максимална величина је 5МБ.",
  popupBlocked: "Попуп прозор је блокиран. Молимо омогућите попупе за ову страницу.",

  // History sidebar
  history: "Историја",
  searchHistory: "Претражи по ХС коду, опису, датуму...",
  noHistory: "Нема претходних класификација.",
  reExportPdf: "📄 ПДФ",

  // Confidence visualization
  confidenceFinal: "ПРОЦЈЕНА",
  confidenceFinalWarning: "ПРОЦЈЕНА СА УПОЗОРЕЊЕМ",

  // Drag and drop
  dragDropHint: "Или превуците слику овдје",
  dragDropActive: "Пустите слику овдје...",

  // Language switcher
  language: "Језик",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * English translations
 */
export const en: TranslationKeys = {
  // Common
  appName: "Customs AI Assistant",
  welcome: "Welcome",
  loading: "Loading...",
  error: "Error",
  success: "Success",
  cancel: "Cancel",
  confirm: "Confirm",
  save: "Save",
  delete: "Delete",
  edit: "Edit",
  search: "Search",
  
  // Navigation
  home: "Home",
  classify: "Classification",
  searchHs: "HS Code Search",
  agreements: "Agreements",
  reports: "Reports",
  admin: "Administration",
  settings: "Settings",
  logout: "Logout",
  
  // Classification
  productDescription: "Product Description",
  additionalInfo: "Additional Information",
  classifyProduct: "Classify Product",
  classificationResult: "Classification Result",
  hsCode: "HS Code",
  confidence: "Confidence",
  confidenceHigh: "High",
  confidenceMedium: "Medium",
  confidenceLow: "Low",
  confidenceNone: "Insufficient",
  legalBasis: "Legal Basis",
  citations: "Citations",
  girPath: "GIR Rules",
  validationReasons: "Validation Reasons",
  
  // Validation status
  statusFinal: "Final",
  statusStop: "Stopped",
  
  // Errors
  errorInvalidHsCode: "Proposed HS code does not exist in the nomenclature",
  errorNoMatch: "Product description does not match the tariff code",
  errorPrecedentConflict: "Precedents suggest a different classification",
  errorHierarchy: "Tariff hierarchical structure is violated",
  
  // Admin
  tenantManagement: "Tenant Management",
  userManagement: "User Management",
  auditLogs: "Audit Logs",
  importData: "Import Data",

  // Chat UI
  chatTitle: "Customs Assistant",
  chatSubtitle: "Fast and accurate HS product classification",
  welcomeTitle: "👋 Welcome!",
  welcomeMessage: "Describe the product you want to classify and I will help you find the appropriate HS (Harmonized System) tariff code.",
  welcomeImageHint: "📷 You can also attach a product image for more precise classification.",
  welcomeExample: '💡 Example: "Seamless steel pipe, diameter 50mm, for construction"',
  newClassification: "+ New Classification",
  sendButton: "Send",
  inputPlaceholder: "Describe the product to classify...",
  inputPlaceholderAdditional: "Enter additional information...",
  linkDetected: "🔗 Link detected - will analyze product page",
  exportPdf: "📄 Export PDF",
  classificationSuccess: "✅ CLASSIFICATION PROPOSAL",
  statusWarning: "⚠️ Status: ASSESSMENT WITH WARNING",
  reasoningLabel: "📋 Reasoning",
  girRulesLabel: "⚖️ GIR Rules",
  legalBasisLabel: "📜 Legal Basis",
  alternativeCodes: "🔄 Alternative Codes",
  modeLabel: "🔧 Mode",
  fromCache: "from cache",
  processingTime: "⏱️ Processing Time",
  toolsUsed: "🛠️ Tools used",
  errorClassify: "⚠️ Unable to classify product. Try with a more detailed description.",
  errorGeneric: "❌ An error occurred. Please try again.",
  imageSelectError: "Please select an image (JPEG, PNG, WebP)",
  imageSizeError: "Image is too large. Maximum size is 5MB.",
  popupBlocked: "Popup window is blocked. Please enable popups for this site.",

  // History sidebar
  history: "History",
  searchHistory: "Search by HS code, description, date...",
  noHistory: "No previous classifications.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "ASSESSMENT",
  confidenceFinalWarning: "ASSESSMENT WITH WARNING",

  // Drag and drop
  dragDropHint: "Or drag and drop an image here",
  dragDropActive: "Drop the image here...",

  // Language switcher
  language: "Language",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * German translations
 */
export const de: TranslationKeys = {
  // Common
  appName: "Zoll-KI-Assistent",
  welcome: "Willkommen",
  loading: "Wird geladen...",
  error: "Fehler",
  success: "Erfolgreich",
  cancel: "Abbrechen",
  confirm: "Bestätigen",
  save: "Speichern",
  delete: "Löschen",
  edit: "Bearbeiten",
  search: "Suchen",
  
  // Navigation
  home: "Startseite",
  classify: "Klassifizierung",
  searchHs: "HS-Code-Suche",
  agreements: "Abkommen",
  reports: "Berichte",
  admin: "Verwaltung",
  settings: "Einstellungen",
  logout: "Abmelden",
  
  // Classification
  productDescription: "Produktbeschreibung",
  additionalInfo: "Zusätzliche Informationen",
  classifyProduct: "Produkt klassifizieren",
  classificationResult: "Klassifizierungsergebnis",
  hsCode: "HS-Code",
  confidence: "Zuverlässigkeit",
  confidenceHigh: "Hoch",
  confidenceMedium: "Mittel",
  confidenceLow: "Niedrig",
  confidenceNone: "Unzureichend",
  legalBasis: "Rechtsgrundlage",
  citations: "Zitate",
  girPath: "GIR-Regeln",
  validationReasons: "Validierungsgründe",
  
  // Validation status
  statusFinal: "Endgültig",
  statusStop: "Gestoppt",
  
  // Errors
  errorInvalidHsCode: "Der vorgeschlagene HS-Code existiert nicht in der Nomenklatur",
  errorNoMatch: "Die Produktbeschreibung stimmt nicht mit der Tarifnummer überein",
  errorPrecedentConflict: "Präzedenzfälle deuten auf eine andere Klassifizierung hin",
  errorHierarchy: "Die hierarchische Struktur des Tarifs ist verletzt",
  
  // Admin
  tenantManagement: "Mandantenverwaltung",
  userManagement: "Benutzerverwaltung",
  auditLogs: "Prüfprotokolle",
  importData: "Daten importieren",

  // Chat UI
  chatTitle: "Zoll-Assistent",
  chatSubtitle: "Schnelle und genaue HS-Produktklassifizierung",
  welcomeTitle: "👋 Willkommen!",
  welcomeMessage: "Beschreiben Sie das Produkt, das Sie klassifizieren möchten, und ich helfe Ihnen, den passenden HS-Tarifcode zu finden.",
  welcomeImageHint: "📷 Sie können auch ein Produktbild für eine genauere Klassifizierung anhängen.",
  welcomeExample: '💡 Beispiel: "Nahtloses Stahlrohr, Durchmesser 50mm, für den Bau"',
  newClassification: "+ Neue Klassifizierung",
  sendButton: "Senden",
  inputPlaceholder: "Beschreiben Sie das Produkt zur Klassifizierung...",
  inputPlaceholderAdditional: "Geben Sie zusätzliche Informationen ein...",
  linkDetected: "🔗 Link erkannt - Produktseite wird analysiert",
  exportPdf: "📄 PDF exportieren",
  classificationSuccess: "✅ KLASSIFIZIERUNGSVORSCHLAG",
  statusWarning: "⚠️ Status: BEWERTUNG MIT WARNUNG",
  reasoningLabel: "📋 Begründung",
  girRulesLabel: "⚖️ GIR-Regeln",
  legalBasisLabel: "📜 Rechtsgrundlage",
  alternativeCodes: "🔄 Alternative Codes",
  modeLabel: "🔧 Modus",
  fromCache: "aus dem Cache",
  processingTime: "⏱️ Verarbeitungszeit",
  toolsUsed: "🛠️ Verwendete Werkzeuge",
  errorClassify: "⚠️ Produkt kann nicht klassifiziert werden. Versuchen Sie es mit einer detaillierteren Beschreibung.",
  errorGeneric: "❌ Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
  imageSelectError: "Bitte wählen Sie ein Bild (JPEG, PNG, WebP)",
  imageSizeError: "Das Bild ist zu groß. Maximale Größe ist 5MB.",
  popupBlocked: "Popup-Fenster ist blockiert. Bitte aktivieren Sie Popups für diese Seite.",

  // History sidebar
  history: "Verlauf",
  searchHistory: "Suche nach HS-Code, Beschreibung, Datum...",
  noHistory: "Keine vorherigen Klassifizierungen.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "BEWERTUNG",
  confidenceFinalWarning: "BEWERTUNG MIT WARNUNG",

  // Drag and drop
  dragDropHint: "Oder Bild hierher ziehen",
  dragDropActive: "Bild hier ablegen...",

  // Language switcher
  language: "Sprache",

  // Classification labels
  proposedHsCode: "Vorgeschlagener HS-Code",
  aiGeneratedDisclaimer: "⚠️ Diese Klassifizierung wurde KI-generiert und dient ausschließlich Informationszwecken. Sie ist rechtlich nicht verbindlich. Bitte prüfen Sie sie vor der Verwendung stets mit der zuständigen Zollbehörde oder einem Zollfachmann.",
};

/**
 * French translations
 */
export const fr: TranslationKeys = {
  // Common
  appName: "Assistant Douanier IA",
  welcome: "Bienvenue",
  loading: "Chargement...",
  error: "Erreur",
  success: "Succès",
  cancel: "Annuler",
  confirm: "Confirmer",
  save: "Enregistrer",
  delete: "Supprimer",
  edit: "Modifier",
  search: "Rechercher",

  // Navigation
  home: "Accueil",
  classify: "Classification",
  searchHs: "Recherche de code SH",
  agreements: "Accords",
  reports: "Rapports",
  admin: "Administration",
  settings: "Paramètres",
  logout: "Déconnexion",

  // Classification
  productDescription: "Description du produit",
  additionalInfo: "Informations supplémentaires",
  classifyProduct: "Classer le produit",
  classificationResult: "Résultat de la classification",
  hsCode: "Code SH",
  confidence: "Fiabilité",
  confidenceHigh: "Élevée",
  confidenceMedium: "Moyenne",
  confidenceLow: "Faible",
  confidenceNone: "Insuffisante",
  legalBasis: "Base juridique",
  citations: "Citations",
  girPath: "Règles GIR",
  validationReasons: "Raisons de validation",

  // Validation status
  statusFinal: "Final",
  statusStop: "Arrêté",

  // Errors
  errorInvalidHsCode: "Le code SH proposé n'existe pas dans la nomenclature",
  errorNoMatch: "La description du produit ne correspond pas au code tarifaire",
  errorPrecedentConflict: "Les précédents suggèrent une classification différente",
  errorHierarchy: "La structure hiérarchique du tarif est violée",

  // Admin
  tenantManagement: "Gestion des locataires",
  userManagement: "Gestion des utilisateurs",
  auditLogs: "Journaux d'audit",
  importData: "Importer des données",

  // Chat UI
  chatTitle: "Assistant Douanier",
  chatSubtitle: "Classification SH rapide et précise des produits",
  welcomeTitle: "👋 Bienvenue !",
  welcomeMessage: "Décrivez le produit que vous souhaitez classer et je vous aiderai à trouver le code tarifaire SH approprié.",
  welcomeImageHint: "📷 Vous pouvez également joindre une image du produit pour une classification plus précise.",
  welcomeExample: '💡 Exemple : "Tube en acier sans soudure, diamètre 50mm, pour la construction"',
  newClassification: "+ Nouvelle classification",
  sendButton: "Envoyer",
  inputPlaceholder: "Décrivez le produit à classer...",
  inputPlaceholderAdditional: "Entrez des informations supplémentaires...",
  linkDetected: "🔗 Lien détecté - analyse de la page produit",
  exportPdf: "📄 Exporter PDF",
  classificationSuccess: "✅ PROPOSITION DE CLASSIFICATION",
  statusWarning: "⚠️ Statut : ÉVALUATION AVEC AVERTISSEMENT",
  reasoningLabel: "📋 Raisonnement",
  girRulesLabel: "⚖️ Règles GIR",
  legalBasisLabel: "📜 Base juridique",
  alternativeCodes: "🔄 Codes alternatifs",
  modeLabel: "🔧 Mode",
  fromCache: "depuis le cache",
  processingTime: "⏱️ Temps de traitement",
  toolsUsed: "🛠️ Outils utilisés",
  errorClassify: "⚠️ Impossible de classer le produit. Essayez avec une description plus détaillée.",
  errorGeneric: "❌ Une erreur s'est produite. Veuillez réessayer.",
  imageSelectError: "Veuillez sélectionner une image (JPEG, PNG, WebP)",
  imageSizeError: "L'image est trop volumineuse. Taille maximale : 5 Mo.",
  popupBlocked: "La fenêtre popup est bloquée. Veuillez activer les popups pour ce site.",

  // History sidebar
  history: "Historique",
  searchHistory: "Rechercher par code SH, description, date...",
  noHistory: "Aucune classification précédente.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "ÉVALUATION",
  confidenceFinalWarning: "ÉVALUATION AVEC AVERTISSEMENT",

  // Drag and drop
  dragDropHint: "Ou glissez-déposez une image ici",
  dragDropActive: "Déposez l'image ici...",

  // Language switcher
  language: "Langue",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * Slovak translations
 */
export const sk: TranslationKeys = {
  // Common
  appName: "Colný AI Asistent",
  welcome: "Vitajte",
  loading: "Načítava sa...",
  error: "Chyba",
  success: "Úspešné",
  cancel: "Zrušiť",
  confirm: "Potvrdiť",
  save: "Uložiť",
  delete: "Odstrániť",
  edit: "Upraviť",
  search: "Hľadať",

  // Navigation
  home: "Domov",
  classify: "Klasifikácia",
  searchHs: "Vyhľadávanie HS kódov",
  agreements: "Dohody",
  reports: "Správy",
  admin: "Administrácia",
  settings: "Nastavenia",
  logout: "Odhlásiť",

  // Classification
  productDescription: "Popis produktu",
  additionalInfo: "Ďalšie informácie",
  classifyProduct: "Klasifikovať produkt",
  classificationResult: "Výsledok klasifikácie",
  hsCode: "HS kód",
  confidence: "Spoľahlivosť",
  confidenceHigh: "Vysoká",
  confidenceMedium: "Stredná",
  confidenceLow: "Nízka",
  confidenceNone: "Nedostatočná",
  legalBasis: "Právny základ",
  citations: "Citácie",
  girPath: "GIR pravidlá",
  validationReasons: "Dôvody validácie",

  // Validation status
  statusFinal: "Konečné",
  statusStop: "Zastavené",

  // Errors
  errorInvalidHsCode: "Navrhnutý HS kód neexistuje v nomenklatúre",
  errorNoMatch: "Popis produktu nezodpovedá colnému kódu",
  errorPrecedentConflict: "Precedenty naznačujú inú klasifikáciu",
  errorHierarchy: "Hierarchická štruktúra tarifu je narušená",

  // Admin
  tenantManagement: "Správa nájomcov",
  userManagement: "Správa používateľov",
  auditLogs: "Audítorské záznamy",
  importData: "Import dát",

  // Chat UI
  chatTitle: "Colný Asistent",
  chatSubtitle: "Rýchla a presná HS klasifikácia produktov",
  welcomeTitle: "👋 Vitajte!",
  welcomeMessage: "Popíšte produkt, ktorý chcete klasifikovať, a ja vám pomôžem nájsť príslušný HS tarifný kód.",
  welcomeImageHint: "📷 Pre presnejšiu klasifikáciu môžete priložiť aj obrázok produktu.",
  welcomeExample: '💡 Príklad: "Bezšvíková oceľová rúra, priemer 50mm, pre stavebníctvo"',
  newClassification: "+ Nová klasifikácia",
  sendButton: "Odoslať",
  inputPlaceholder: "Popíšte produkt na klasifikáciu...",
  inputPlaceholderAdditional: "Zadajte ďalšie informácie...",
  linkDetected: "🔗 Odkaz zistený - analyzujem stránku produktu",
  exportPdf: "📄 Export PDF",
  classificationSuccess: "✅ NÁVRH KLASIFIKÁCIE",
  statusWarning: "⚠️ Stav: HODNOTENIE S UPOZORNENÍM",
  reasoningLabel: "📋 Odôvodnenie",
  girRulesLabel: "⚖️ GIR pravidlá",
  legalBasisLabel: "📜 Právny základ",
  alternativeCodes: "🔄 Alternatívne kódy",
  modeLabel: "🔧 Režim",
  fromCache: "z vyrovnávacej pamäte",
  processingTime: "⏱️ Čas spracovania",
  toolsUsed: "🛠️ Použité nástroje",
  errorClassify: "⚠️ Produkt nie je možné klasifikovať. Skúste podrobnejší popis.",
  errorGeneric: "❌ Vyskytla sa chyba. Skúste to znova.",
  imageSelectError: "Vyberte obrázok (JPEG, PNG, WebP)",
  imageSizeError: "Obrázok je príliš veľký. Maximálna veľkosť je 5MB.",
  popupBlocked: "Vyskakovacie okno je zablokované. Povoľte vyskakovacie okná pre túto stránku.",

  // History sidebar
  history: "História",
  searchHistory: "Hľadať podľa HS kódu, popisu, dátumu...",
  noHistory: "Žiadne predchádzajúce klasifikácie.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "HODNOTENIE",
  confidenceFinalWarning: "HODNOTENIE S UPOZORNENÍM",

  // Drag and drop
  dragDropHint: "Alebo sem pretiahnite obrázok",
  dragDropActive: "Pustite obrázok sem...",

  // Language switcher
  language: "Jazyk",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * Italian translations
 */
export const it: TranslationKeys = {
  // Common
  appName: "Assistente Doganale IA",
  welcome: "Benvenuto",
  loading: "Caricamento...",
  error: "Errore",
  success: "Successo",
  cancel: "Annulla",
  confirm: "Conferma",
  save: "Salva",
  delete: "Elimina",
  edit: "Modifica",
  search: "Cerca",

  // Navigation
  home: "Home",
  classify: "Classificazione",
  searchHs: "Ricerca codice SA",
  agreements: "Accordi",
  reports: "Report",
  admin: "Amministrazione",
  settings: "Impostazioni",
  logout: "Esci",

  // Classification
  productDescription: "Descrizione del prodotto",
  additionalInfo: "Informazioni aggiuntive",
  classifyProduct: "Classifica prodotto",
  classificationResult: "Risultato della classificazione",
  hsCode: "Codice SA",
  confidence: "Affidabilità",
  confidenceHigh: "Alta",
  confidenceMedium: "Media",
  confidenceLow: "Bassa",
  confidenceNone: "Insufficiente",
  legalBasis: "Base giuridica",
  citations: "Citazioni",
  girPath: "Regole GIR",
  validationReasons: "Motivi di validazione",

  // Validation status
  statusFinal: "Finale",
  statusStop: "Fermato",

  // Errors
  errorInvalidHsCode: "Il codice SA proposto non esiste nella nomenclatura",
  errorNoMatch: "La descrizione del prodotto non corrisponde al codice tariffario",
  errorPrecedentConflict: "I precedenti suggeriscono una classificazione diversa",
  errorHierarchy: "La struttura gerarchica della tariffa è violata",

  // Admin
  tenantManagement: "Gestione inquilini",
  userManagement: "Gestione utenti",
  auditLogs: "Registri di audit",
  importData: "Importa dati",

  // Chat UI
  chatTitle: "Assistente Doganale",
  chatSubtitle: "Classificazione SA rapida e precisa dei prodotti",
  welcomeTitle: "👋 Benvenuto!",
  welcomeMessage: "Descrivi il prodotto che vuoi classificare e ti aiuterò a trovare il codice tariffario SA appropriato.",
  welcomeImageHint: "📷 Puoi anche allegare un'immagine del prodotto per una classificazione più precisa.",
  welcomeExample: '💡 Esempio: "Tubo in acciaio senza saldatura, diametro 50mm, per edilizia"',
  newClassification: "+ Nuova classificazione",
  sendButton: "Invia",
  inputPlaceholder: "Descrivi il prodotto da classificare...",
  inputPlaceholderAdditional: "Inserisci informazioni aggiuntive...",
  linkDetected: "🔗 Link rilevato - analisi della pagina prodotto",
  exportPdf: "📄 Esporta PDF",
  classificationSuccess: "✅ PROPOSTA DI CLASSIFICAZIONE",
  statusWarning: "⚠️ Stato: VALUTAZIONE CON AVVISO",
  reasoningLabel: "📋 Ragionamento",
  girRulesLabel: "⚖️ Regole GIR",
  legalBasisLabel: "📜 Base giuridica",
  alternativeCodes: "🔄 Codici alternativi",
  modeLabel: "🔧 Modalità",
  fromCache: "dalla cache",
  processingTime: "⏱️ Tempo di elaborazione",
  toolsUsed: "🛠️ Strumenti utilizzati",
  errorClassify: "⚠️ Impossibile classificare il prodotto. Prova con una descrizione più dettagliata.",
  errorGeneric: "❌ Si è verificato un errore. Riprova.",
  imageSelectError: "Seleziona un'immagine (JPEG, PNG, WebP)",
  imageSizeError: "L'immagine è troppo grande. Dimensione massima: 5MB.",
  popupBlocked: "La finestra popup è bloccata. Abilita i popup per questo sito.",

  // History sidebar
  history: "Cronologia",
  searchHistory: "Cerca per codice SA, descrizione, data...",
  noHistory: "Nessuna classificazione precedente.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "VALUTAZIONE",
  confidenceFinalWarning: "VALUTAZIONE CON AVVISO",

  // Drag and drop
  dragDropHint: "Oppure trascina un'immagine qui",
  dragDropActive: "Rilascia l'immagine qui...",

  // Language switcher
  language: "Lingua",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * Macedonian translations
 */
export const mk: TranslationKeys = {
  // Common
  appName: "Царински АИ Асистент",
  welcome: "Добредојдовте",
  loading: "Се вчитува...",
  error: "Грешка",
  success: "Успешно",
  cancel: "Откажи",
  confirm: "Потврди",
  save: "Зачувај",
  delete: "Избриши",
  edit: "Уреди",
  search: "Пребарај",

  // Navigation
  home: "Почетна",
  classify: "Класификација",
  searchHs: "Пребарување HS кодови",
  agreements: "Договори",
  reports: "Извештаи",
  admin: "Администрација",
  settings: "Поставки",
  logout: "Одјава",

  // Classification
  productDescription: "Опис на производот",
  additionalInfo: "Дополнителни информации",
  classifyProduct: "Класифицирај производ",
  classificationResult: "Резултат од класификација",
  hsCode: "HS код",
  confidence: "Доверливост",
  confidenceHigh: "Висока",
  confidenceMedium: "Средна",
  confidenceLow: "Ниска",
  confidenceNone: "Недоволна",
  legalBasis: "Правна основа",
  citations: "Цитати",
  girPath: "GIR правила",
  validationReasons: "Причини за валидација",

  // Validation status
  statusFinal: "Конечно",
  statusStop: "Запрено",

  // Errors
  errorInvalidHsCode: "Предложениот HS код не постои во номенклатурата",
  errorNoMatch: "Описот на производот не одговара на тарифниот број",
  errorPrecedentConflict: "Претходните случаи сугерираат поинаква класификација",
  errorHierarchy: "Хиерархиската структура на тарифата е нарушена",

  // Admin
  tenantManagement: "Управување со закупци",
  userManagement: "Управување со корисници",
  auditLogs: "Ревизорски записи",
  importData: "Увоз на податоци",

  // Chat UI
  chatTitle: "Царински Асистент",
  chatSubtitle: "Брза и прецизна HS класификација на производи",
  welcomeTitle: "👋 Добредојдовте!",
  welcomeMessage: "Опишете го производот што сакате да го класифицирате, а јас ќе ви помогнам да го пронајдете соодветниот HS тарифен број.",
  welcomeImageHint: "📷 Можете да приложите и слика од производот за попрецизна класификација.",
  welcomeExample: '💡 Пример: "Челична цевка, бесшевна, дијаметар 50мм, за градежништво"',
  newClassification: "+ Нова класификација",
  sendButton: "Испрати",
  inputPlaceholder: "Опишете го производот за класификација...",
  inputPlaceholderAdditional: "Внесете дополнителни информации...",
  linkDetected: "🔗 Линк детектиран - ја анализирам страницата на производот",
  exportPdf: "📄 Извоз PDF",
  classificationSuccess: "✅ ПРЕДЛОГ ЗА КЛАСИФИКАЦИЈА",
  statusWarning: "⚠️ Статус: ПРОЦЕНКА СО ПРЕДУПРЕДУВАЊЕ",
  reasoningLabel: "📋 Образложение",
  girRulesLabel: "⚖️ GIR правила",
  legalBasisLabel: "📜 Правна основа",
  alternativeCodes: "🔄 Алтернативни кодови",
  modeLabel: "🔧 Режим",
  fromCache: "од кеш меморија",
  processingTime: "⏱️ Време на обработка",
  toolsUsed: "🛠️ Користени алатки",
  errorClassify: "⚠️ Не е можно да се класифицира производот. Обидете се со подетален опис.",
  errorGeneric: "❌ Се појави грешка. Обидете се повторно.",
  imageSelectError: "Изберете слика (JPEG, PNG, WebP)",
  imageSizeError: "Сликата е преголема. Максимална големина е 5MB.",
  popupBlocked: "Скокачкиот прозорец е блокиран. Овозможете скокачки прозорци за оваа страница.",

  // History sidebar
  history: "Историја",
  searchHistory: "Пребарај по HS код, опис, датум...",
  noHistory: "Нема претходни класификации.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "ПРОЦЕНКА",
  confidenceFinalWarning: "ПРОЦЕНКА СО ПРЕДУПРЕДУВАЊЕ",

  // Drag and drop
  dragDropHint: "Или повлечете слика овде",
  dragDropActive: "Пуштете ја сликата овде...",

  // Language switcher
  language: "Јазик",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * Russian translations
 */
export const ru: TranslationKeys = {
  // Common
  appName: "Таможенный ИИ Ассистент",
  welcome: "Добро пожаловать",
  loading: "Загрузка...",
  error: "Ошибка",
  success: "Успешно",
  cancel: "Отмена",
  confirm: "Подтвердить",
  save: "Сохранить",
  delete: "Удалить",
  edit: "Редактировать",
  search: "Поиск",

  // Navigation
  home: "Главная",
  classify: "Классификация",
  searchHs: "Поиск кодов HS",
  agreements: "Соглашения",
  reports: "Отчёты",
  admin: "Администрирование",
  settings: "Настройки",
  logout: "Выход",

  // Classification
  productDescription: "Описание товара",
  additionalInfo: "Дополнительная информация",
  classifyProduct: "Классифицировать товар",
  classificationResult: "Результат классификации",
  hsCode: "Код HS",
  confidence: "Достоверность",
  confidenceHigh: "Высокая",
  confidenceMedium: "Средняя",
  confidenceLow: "Низкая",
  confidenceNone: "Недостаточная",
  legalBasis: "Правовая основа",
  citations: "Цитаты",
  girPath: "Правила GIR",
  validationReasons: "Причины валидации",

  // Validation status
  statusFinal: "Окончательно",
  statusStop: "Остановлено",

  // Errors
  errorInvalidHsCode: "Предложенный код HS не существует в номенклатуре",
  errorNoMatch: "Описание товара не соответствует тарифному коду",
  errorPrecedentConflict: "Прецеденты указывают на иную классификацию",
  errorHierarchy: "Иерархическая структура тарифа нарушена",

  // Admin
  tenantManagement: "Управление арендаторами",
  userManagement: "Управление пользователями",
  auditLogs: "Журналы аудита",
  importData: "Импорт данных",

  // Chat UI
  chatTitle: "Таможенный Ассистент",
  chatSubtitle: "Быстрая и точная классификация товаров по HS",
  welcomeTitle: "👋 Добро пожаловать!",
  welcomeMessage: "Опишите товар, который хотите классифицировать, и я помогу вам найти соответствующий тарифный код HS.",
  welcomeImageHint: "📷 Вы также можете прикрепить изображение товара для более точной классификации.",
  welcomeExample: '💡 Пример: "Стальная труба, бесшовная, диаметр 50мм, для строительства"',
  newClassification: "+ Новая классификация",
  sendButton: "Отправить",
  inputPlaceholder: "Опишите товар для классификации...",
  inputPlaceholderAdditional: "Введите дополнительную информацию...",
  linkDetected: "🔗 Обнаружена ссылка — анализирую страницу товара",
  exportPdf: "📄 Экспорт PDF",
  classificationSuccess: "✅ ПРЕДЛОЖЕНИЕ ПО КЛАССИФИКАЦИИ",
  statusWarning: "⚠️ Статус: ОЦЕНКА С ПРЕДУПРЕЖДЕНИЕМ",
  reasoningLabel: "📋 Обоснование",
  girRulesLabel: "⚖️ Правила GIR",
  legalBasisLabel: "📜 Правовая основа",
  alternativeCodes: "🔄 Альтернативные коды",
  modeLabel: "🔧 Режим",
  fromCache: "из кэша",
  processingTime: "⏱️ Время обработки",
  toolsUsed: "🛠️ Использованные инструменты",
  errorClassify: "⚠️ Не удалось классифицировать товар. Попробуйте более подробное описание.",
  errorGeneric: "❌ Произошла ошибка. Пожалуйста, попробуйте снова.",
  imageSelectError: "Выберите изображение (JPEG, PNG, WebP)",
  imageSizeError: "Изображение слишком большое. Максимальный размер — 5МБ.",
  popupBlocked: "Всплывающее окно заблокировано. Разрешите всплывающие окна для этого сайта.",

  // History sidebar
  history: "История",
  searchHistory: "Поиск по коду HS, описанию, дате...",
  noHistory: "Нет предыдущих классификаций.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "ОЦЕНКА",
  confidenceFinalWarning: "ОЦЕНКА С ПРЕДУПРЕЖДЕНИЕМ",

  // Drag and drop
  dragDropHint: "Или перетащите изображение сюда",
  dragDropActive: "Отпустите изображение здесь...",

  // Language switcher
  language: "Язык",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * Spanish translations
 */
export const es: TranslationKeys = {
  // Common
  appName: "Asistente Aduanero IA",
  welcome: "Bienvenido",
  loading: "Cargando...",
  error: "Error",
  success: "Éxito",
  cancel: "Cancelar",
  confirm: "Confirmar",
  save: "Guardar",
  delete: "Eliminar",
  edit: "Editar",
  search: "Buscar",

  // Navigation
  home: "Inicio",
  classify: "Clasificación",
  searchHs: "Búsqueda de códigos SA",
  agreements: "Acuerdos",
  reports: "Informes",
  admin: "Administración",
  settings: "Configuración",
  logout: "Cerrar sesión",

  // Classification
  productDescription: "Descripción del producto",
  additionalInfo: "Información adicional",
  classifyProduct: "Clasificar producto",
  classificationResult: "Resultado de la clasificación",
  hsCode: "Código SA",
  confidence: "Confianza",
  confidenceHigh: "Alta",
  confidenceMedium: "Media",
  confidenceLow: "Baja",
  confidenceNone: "Insuficiente",
  legalBasis: "Base legal",
  citations: "Citas",
  girPath: "Reglas GIR",
  validationReasons: "Razones de validación",

  // Validation status
  statusFinal: "Final",
  statusStop: "Detenido",

  // Errors
  errorInvalidHsCode: "El código SA propuesto no existe en la nomenclatura",
  errorNoMatch: "La descripción del producto no coincide con el código arancelario",
  errorPrecedentConflict: "Los precedentes sugieren una clasificación diferente",
  errorHierarchy: "La estructura jerárquica del arancel está violada",

  // Admin
  tenantManagement: "Gestión de inquilinos",
  userManagement: "Gestión de usuarios",
  auditLogs: "Registros de auditoría",
  importData: "Importar datos",

  // Chat UI
  chatTitle: "Asistente Aduanero",
  chatSubtitle: "Clasificación SA rápida y precisa de productos",
  welcomeTitle: "👋 ¡Bienvenido!",
  welcomeMessage: "Describa el producto que desea clasificar y le ayudaré a encontrar el código arancelario SA correspondiente.",
  welcomeImageHint: "📷 También puede adjuntar una imagen del producto para una clasificación más precisa.",
  welcomeExample: '💡 Ejemplo: "Tubo de acero sin costura, diámetro 50mm, para construcción"',
  newClassification: "+ Nueva clasificación",
  sendButton: "Enviar",
  inputPlaceholder: "Describa el producto a clasificar...",
  inputPlaceholderAdditional: "Introduzca información adicional...",
  linkDetected: "🔗 Enlace detectado — analizando la página del producto",
  exportPdf: "📄 Exportar PDF",
  classificationSuccess: "✅ PROPUESTA DE CLASIFICACIÓN",
  statusWarning: "⚠️ Estado: EVALUACIÓN CON ADVERTENCIA",
  reasoningLabel: "📋 Razonamiento",
  girRulesLabel: "⚖️ Reglas GIR",
  legalBasisLabel: "📜 Base legal",
  alternativeCodes: "🔄 Códigos alternativos",
  modeLabel: "🔧 Modo",
  fromCache: "desde caché",
  processingTime: "⏱️ Tiempo de procesamiento",
  toolsUsed: "🛠️ Herramientas utilizadas",
  errorClassify: "⚠️ No se pudo clasificar el producto. Intente con una descripción más detallada.",
  errorGeneric: "❌ Se produjo un error. Por favor, inténtelo de nuevo.",
  imageSelectError: "Seleccione una imagen (JPEG, PNG, WebP)",
  imageSizeError: "La imagen es demasiado grande. Tamaño máximo: 5MB.",
  popupBlocked: "La ventana emergente está bloqueada. Habilite las ventanas emergentes para este sitio.",

  // History sidebar
  history: "Historial",
  searchHistory: "Buscar por código SA, descripción, fecha...",
  noHistory: "No hay clasificaciones anteriores.",
  reExportPdf: "📄 PDF",

  // Confidence visualization
  confidenceFinal: "EVALUACIÓN",
  confidenceFinalWarning: "EVALUACIÓN CON ADVERTENCIA",

  // Drag and drop
  dragDropHint: "O arrastre y suelte una imagen aquí",
  dragDropActive: "Suelte la imagen aquí...",

  // Language switcher
  language: "Idioma",

  // Classification labels
  proposedHsCode: "Proposed HS Code",
  aiGeneratedDisclaimer: "⚠️ This classification is AI-generated and for informational purposes only. It is not legally binding. Always verify with the competent customs authority or a customs expert before use.",
};

/**
 * All translations
 */
export const translations: Record<SupportedLanguage, TranslationKeys> = {
  bs: bs,
  "bs-Cyrl": bsCyrl,
  en: en,
  de: de,
  sr: bs, // Serbian Latin same as Bosnian Latin
  "sr-Cyrl": bsCyrl, // Serbian Cyrillic same as Bosnian Cyrillic
  fr: fr,
  sk: sk,
  it: it,
  mk: mk,
  ru: ru,
  es: es,
};

/**
 * Get translation for a specific language
 */
export function getTranslation(language: SupportedLanguage): TranslationKeys {
  return translations[language] || translations.en;
}

/**
 * Translate a key
 */
export function t(key: keyof TranslationKeys, language: SupportedLanguage = "bs"): string {
  const translation = translations[language] || translations.en;
  return translation[key] || key;
}

/**
 * Language display names (for language switcher)
 */
export const languageNames: Record<SupportedLanguage, string> = {
  bs: "Bosanski",
  "bs-Cyrl": "Босански",
  en: "English",
  de: "Deutsch",
  sr: "Srpski",
  "sr-Cyrl": "Српски",
  fr: "Français",
  sk: "Slovenčina",
  it: "Italiano",
  mk: "Македонски",
  ru: "Русский",
  es: "Español",
};

/**
 * Script conversion utilities
 */

/**
 * Latin to Cyrillic mapping for Bosnian/Serbian
 */
const latinToCyrillic: Record<string, string> = {
  "a": "а", "b": "б", "c": "ц", "č": "ч", "ć": "ћ",
  "d": "д", "đ": "ђ", "dž": "џ", "e": "е", "f": "ф",
  "g": "г", "h": "х", "i": "и", "j": "ј", "k": "к",
  "l": "л", "lj": "љ", "m": "м", "n": "н", "nj": "њ",
  "o": "о", "p": "п", "r": "р", "s": "с", "š": "ш",
  "t": "т", "u": "у", "v": "в", "z": "з", "ž": "ж",
  "A": "А", "B": "Б", "C": "Ц", "Č": "Ч", "Ć": "Ћ",
  "D": "Д", "Đ": "Ђ", "DŽ": "Џ", "E": "Е", "F": "Ф",
  "G": "Г", "H": "Х", "I": "И", "J": "Ј", "K": "К",
  "L": "Л", "LJ": "Љ", "M": "М", "N": "Н", "NJ": "Њ",
  "O": "О", "P": "П", "R": "Р", "S": "С", "Š": "Ш",
  "T": "Т", "U": "У", "V": "В", "Z": "З", "Ž": "Ж",
};

/**
 * Convert Latin text to Cyrillic
 */
export function toCyrillic(text: string): string {
  let result = "";
  let i = 0;
  
  while (i < text.length) {
    // Check for digraphs (lj, nj, dž)
    if (i < text.length - 1) {
      const digraph = text.substring(i, i + 2).toLowerCase();
      if (digraph === "lj" || digraph === "nj" || digraph === "dž") {
        const isUpper = text[i] === text[i].toUpperCase();
        result += isUpper ? latinToCyrillic[digraph.toUpperCase()] || latinToCyrillic[digraph] : latinToCyrillic[digraph];
        i += 2;
        continue;
      }
    }
    
    const char = text[i];
    result += latinToCyrillic[char] || char;
    i++;
  }
  
  return result;
}

/**
 * Convert Cyrillic text to Latin
 */
export function toLatin(text: string): string {
  const cyrillicToLatin: Record<string, string> = {};
  for (const [latin, cyrillic] of Object.entries(latinToCyrillic)) {
    cyrillicToLatin[cyrillic] = latin;
  }
  
  let result = "";
  for (const char of text) {
    result += cyrillicToLatin[char] || char;
  }
  
  return result;
}

/**
 * Normalize input to standard script (Latin) before processing
 */
export function normalizeScript(text: string): string {
  // Detect if text contains Cyrillic characters
  const cyrillicPattern = /[\u0400-\u04FF]/;
  if (cyrillicPattern.test(text)) {
    return toLatin(text);
  }
  return text;
}
